import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'

import { yayApi } from 'store/api/baseApi'

const apiMapper = {
  yayApi,
}

type Props<T = any> = {
  queryName: string
  memoArg?: T
  dataKey?: string
  skip?: boolean
  paginationKeyName?: string
  apiKey?: keyof typeof apiMapper
}

export const useInfiniteScrollQuery = <ResultType = any, ParamsType = any>({
  queryName,
  memoArg,
  dataKey,
  skip = false,
  paginationKeyName = 'fromId',
  apiKey = 'yayApi',
}: Props<ParamsType>) => {
  const apiEndpoints = apiMapper[apiKey].endpoints[queryName]
  const dispatch = useDispatch()
  const [loadFrom, setLoadFrom] = useState<string | number>()
  const query: any = useSelector(apiEndpoints.select(memoArg))
  const latestQuery: any = useSelector(
    apiEndpoints.select({
      ...memoArg,
      [paginationKeyName]: loadFrom,
    })
  )

  useEffect(() => {
    setLoadFrom(undefined)
  }, [memoArg])

  useEffect(() => {
    if (skip) return

    const result = dispatch(apiEndpoints.initiate(memoArg))

    return () => {
      result.unsubscribe()
    }
  }, [dispatch, memoArg, skip])

  useEffect(() => {
    if (
      loadFrom !== null &&
      typeof loadFrom !== 'undefined' &&
      typeof memoArg !== 'undefined' &&
      !skip
    ) {
      const result = dispatch(
        apiEndpoints.initiate({
          ...memoArg,
          [paginationKeyName]: loadFrom,
        })
      )

      return () => {
        result.unsubscribe()
      }
    }
  }, [dispatch, loadFrom, memoArg, skip])

  const queries = useSelector((state) => state[apiKey].queries)

  const combinedResults = useMemo<ResultType[]>(() => {
    let results = []

    for (let q in queries) {
      const queryDetail = queries[q]
      if (
        queryDetail.endpointName === queryName &&
        isEqual(pick(queryDetail.originalArgs, Object.keys(memoArg)), memoArg)
      ) {
        const data =
          typeof dataKey === 'undefined'
            ? queryDetail?.data
            : queryDetail?.data?.[dataKey]

        if (data) {
          results = results.concat(data)
        }
      }
    }

    return results
  }, [memoArg, queryName, queries, loadFrom])

  const latestData =
    (dataKey ? latestQuery.data?.[dataKey] : latestQuery.data) || []
  const shouldLoadMore: boolean =
    loadFrom !== null &&
    (latestQuery.isUninitialized || latestData.length !== 0)

  const resultData: {
    loadMore: (loadFromId: string | number) => void
    shouldLoadMore: boolean
    latestQuery: any
    combinedResults: ResultType[]
    isLoading: boolean
    updateDataById: (updateData: Partial<ResultType>) => void
    [key: string]: any
  } = {
    loadMore: setLoadFrom,
    shouldLoadMore,
    latestQuery,
    combinedResults,
    ...query,
    isLoading: latestQuery.isLoading,
  }

  return resultData
}
